import type { ApiResponse } from '@creator/sdk/creator-sdk';
import { post as postApi } from '@creator/sdk/network/api-service';
import creatorSdk from '@src/services/creator-sdk';
import { PostStatus, type Post } from '@src/store/contentEntities/model';
import { getPost, setPost } from './posts';
import type { CreatePostApiRequestPayload } from './create';

export type ManageDraftPostAction = 'draft' | 'schedule' | 'publishNow' | 'discardDraft';
export interface ManageDraftPostApiRequestPayload {
    userId: string;
    tokenName: string;
    postId: string;
    action: ManageDraftPostAction;
    scheduledAt?: string; // only for reschedule
}

export interface CreateDraftPayload extends CreatePostApiRequestPayload {}

// New draft creation API
export const createDraft = async (payload: CreateDraftPayload) => {
    const authIdToken = await creatorSdk.accountModule.getIdToken();

    const updatedPayload = { ...payload, saveAsDraft: true };

    const { data } = await postApi<ApiResponse<Post>>(
        '/post/create',
        updatedPayload,
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    const post = data.payload;
    console.log('[DraftAPI] Draft created successfully:', {
        postId: post.id,
        status: post.status,
        hasSlug: !!post.slugUrl,
        title: post.title,
    });

    // sync updated post into Jotai
    setPost(post);
    return post;
};

// New draft update API
export const updateDraft = async (payload: CreateDraftPayload) => {
    const authIdToken = await creatorSdk.accountModule.getIdToken();

    const { data } = await postApi<ApiResponse<Post>>(
        '/post/edit',
        payload,
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    const post = data.payload;

    // sync updated post into Jotai
    // setPost(post);
    return post;
};

// Unified create or update function
export const createOrUpdateDraft = async (payload: CreateDraftPayload) => {
    // If postId is provided, update existing draft; otherwise create new
    if (payload.postId) return updateDraft(payload);
    return createDraft(payload);
};

/**
 * Publish a draft post immediately
 */
export const publishDraft = async (postId: string, tokenName: string, userId: string) => {
    const authIdToken = await creatorSdk.accountModule.getIdToken();

    const { data } = await postApi<ApiResponse<Post>>(
        '/post/drafts/publish',
        {
            postId,
            tokenName,
            userId,
        },
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    const post = data.payload;
    console.log('[DraftAPI] Draft published successfully:', {
        postId: post.id,
        status: post.status,
        title: post.title,
    });

    // sync updated post into Jotai
    setPost(post);

    return post;
};

/**
 * Schedule a draft post for later publication
 */
export const scheduleDraft = async (postId: string, tokenName: string, userId: string, scheduledAt: string) => {
    const authIdToken = await creatorSdk.accountModule.getIdToken();

    const { data } = await postApi<ApiResponse<Post>>(
        '/post/drafts/schedule',
        {
            postId,
            tokenName,
            userId,
            scheduledAt,
        },
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    const post = data.payload;
    console.log('[DraftAPI] Draft scheduled successfully:', {
        postId: post.id,
        status: post.status,
        scheduledAt: post.scheduledAt,
        title: post.title,
    });

    // sync updated post into Jotai
    setPost(post);

    return post;
};

/**
 * Unschedule a scheduled draft post (convert back to draft)
 */
export const unscheduleDraft = async (postId: string, tokenName: string, userId: string) => {
    const authIdToken = await creatorSdk.accountModule.getIdToken();

    const { data } = await postApi<ApiResponse<Post>>(
        '/post/drafts/unschedule',
        {
            postId,
            tokenName,
            userId,
        },
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    const post = data.payload;
    console.log('[DraftAPI] Draft unscheduled successfully:', {
        postId: post.id,
        status: post.status,
        title: post.title,
    });

    // sync updated post into Jotai
    setPost(post);

    return post;
};

/**
 * Delete a draft post
 */
export const deleteDraft = async (postId: string, tokenName: string, userId: string) => {
    const authIdToken = await creatorSdk.accountModule.getIdToken();
    const post = getPost(postId);

    const { data } = await postApi<ApiResponse<Post>>(
        '/post/drafts/delete',
        {
            postId,
            tokenName,
            userId,
        },
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    setPost({ ...post, status: PostStatus.DELETED_BY_PUBLISHER } as Post);

    return data.payload;
};

export const manageDraftPost = async (payload: ManageDraftPostApiRequestPayload) => {
    const { postId, tokenName, userId, action, scheduledAt } = payload;

    console.log('[DraftAPI] Managing draft post:', {
        postId,
        tokenName,
        userId,
        action,
        scheduledAt,
    });

    let post: Post;

    switch (action) {
        case 'publishNow':
            post = await publishDraft(postId, tokenName, userId);
            break;
        case 'schedule':
            if (!scheduledAt) throw new Error('scheduledAt is required for schedule action');
            post = await scheduleDraft(postId, tokenName, userId, scheduledAt);
            break;
        case 'draft':
            post = await unscheduleDraft(postId, tokenName, userId);
            break;
        case 'discardDraft':
            post = await deleteDraft(postId, tokenName, userId);
            break;
        default:
            throw new Error(`Unknown action: ${action}`);
    }

    return post;
};

// Validation for scheduledAt, date must be in the future and within one month from now
export const isScheduledAtValid = (scheduledAt: string) => {
    const scheduledDate = new Date(scheduledAt);
    const now = new Date();
    const oneMonthLater = new Date();
    oneMonthLater.setMonth(now.getMonth() + 1);

    // Check if valid date object, if it's after now, and before one month from now
    return !Number.isNaN(scheduledDate.getTime()) && scheduledDate > now && scheduledDate < oneMonthLater;
};
